import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Calculator from "./components/Calculator";
// Hinweis: LandingPage.jsx ist nicht mehr im Routing — Marketing liegt auf offfer.de (Astro).
// Auf app.offfer.de zeigen wir direkt AppEntry (Login/Register-Karte).
import AppEntry from "./components/AppEntry";
import SuperAdminDashboard from "./components/SuperAdminDashboard";
import DataManagement from "./components/DataManagement";
import PublicQuoteView from "./components/PublicQuoteView";
import AIChatbot from "./components/AIChatbot";
import ResetPassword from "./components/ResetPassword";
import TrialBanner from "./components/TrialBanner";
import PublicDocumentSign from "./components/PublicDocumentSign";
import Impressum from "./components/legal/Impressum";
import Datenschutz from "./components/legal/Datenschutz";
import AGB from "./components/legal/AGB";

// Auth Context
const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [isTrial, setIsTrial] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check for existing auth on app load
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      setIsTrial(localStorage.getItem('isTrialSession') === 'true');
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // Verify token validity AND refresh user record (Tier/Role-Updates).
        // Bug-Fix: vorher wurde localStorage NICHT refreshed → wenn Admin den
        // Tier oder die Rolle eines bestehenden Logins änderte, sah das Frontend
        // weiter den alten Tier. Jetzt wird jeder erfolgreiche /me-Call ins
        // localStorage zurückgeschrieben.
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(response => {
          if (!response.ok) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            setUser(null);
            return null;
          }
          return response.json();
        })
        .then(freshUser => {
          if (freshUser) {
            localStorage.setItem('userData', JSON.stringify(freshUser));
            setUser(freshUser);
          }
        })
        .catch(() => {
          // Network error, keep user logged in for offline use
        });
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    }
    setLoading(false);
  }, []);


  // Trial cleanup: delete sandbox tenant when tab closes
  useEffect(() => {
    if (!isTrial) return
    const handleBeforeUnload = () => {
      const token = localStorage.getItem('authToken')
      if (!token) return
      // Use sendBeacon for reliable delivery on tab close
      const blob = new Blob([JSON.stringify({ token })], { type: 'application/json' })
      navigator.sendBeacon('/api/trial/end', blob)
      localStorage.removeItem('authToken')
      localStorage.removeItem('userData')
      localStorage.removeItem('isTrialSession')
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isTrial])

  const handleLogin = (userData) => {
    setUser(userData);
    setIsTrial(userData?.is_trial === true || localStorage.getItem('isTrialSession') === 'true');
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('isTrialSession');
    sessionStorage.clear();
    setUser(null);
    setIsTrial(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Lade Anwendung...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, handleLogin, handleLogout }}>
      <div className="App">
        {user && isTrial && (
          <TrialBanner
            onUpgrade={() => {
              if (confirm('Account erstellen? Deine Demo-Daten gehen verloren.')) {
                handleLogout();
                window.location.href = '/';
              }
            }}
          />
        )}
        <BrowserRouter>
          <Routes>
            {/* Protected Routes - require authentication */}
            <Route 
              path="/app" 
              element={
                user ? (
                  user.role === 'super_admin' ? (
                    <Navigate to="/admin" replace />
                  ) : (
                    <Calculator user={user} onLogout={handleLogout} />
                  )
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
            
            {/* Data Management - Protected Route */}
            <Route 
              path="/data-management" 
              element={
                user ? (
                  <DataManagement />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
            
            {/* Super Admin Dashboard */}
            <Route 
              path="/admin" 
              element={
                user?.role === 'super_admin' ? (
                  <SuperAdminDashboard currentUser={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to={user ? "/app" : "/"} replace />
                )
              } 
            />
            
            {/* Public Routes */}
            <Route 
              path="/" 
              element={
                !user ? (
                  <AppEntry onLogin={handleLogin} />
                ) : (
                  <Navigate to={user.role === 'super_admin' ? "/admin" : "/app"} replace />
                )
              } 
            />
            
            {/* Public Quote View - No authentication required */}
            <Route 
              path="/quote/view/:viewHash" 
              element={<PublicQuoteView />} 
            />
            
            {/* Password Reset - Public Route */}
            <Route
              path="/reset-password"
              element={
                <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                    <div className="p-2 pb-0 text-center pt-6">
                      <div className="text-2xl font-bold tracking-tight text-slate-900 mb-4">offfer</div>
                    </div>
                    <ResetPassword onSuccess={() => window.location.href = '/?reset=success'} />
                  </div>
                </div>
              }
            />

            {/* Public Document Signing - No authentication required */}
            <Route
              path="/documents/sign/:viewHash"
              element={<PublicDocumentSign />}
            />

            {/* Legal Pages - Public */}
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/agb" element={<AGB />} />

            {/* Fallback for unknown routes */}
            <Route 
              path="*" 
              element={<Navigate to={user ? "/app" : "/"} replace />} 
            />
          </Routes>
          <Toaster position="top-right" />
          
          {/* AI Chatbot - Always visible when logged in */}
          {user && <AIChatbot />}
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export { AuthContext };
export default App;